import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
  CUSTOM_CODE_DIALOG_CONFIG,
  CustomCodeDialogData,
  CustomCodeDialogResult,
  SmartCartFormCustomCodeDialogComponent,
} from '../../widget-editors/custom-code/custom-code-dialog.component';
import { MonacoEditorLanguage } from '../../../components/monaco-editor/monaco-editor.component';
import { CustomCodeVariableDoc } from '../../widget-editors/custom-code/custom-code.model';
import { SmartCartFormCustomTemplateConfirmDialogComponent } from './custom-template-confirm-dialog.component';
import {
  CALLBACK_STARTERS,
  CALLBACK_VARIABLES,
  CODE_SLOT_KEYS_IN_DISPLAY_ORDER,
  CODE_SLOT_LABELS,
  CodeSlotKey,
  CUSTOM_CSS_STARTER,
  EventCallbackKey,
  GLOBAL_CUSTOM_TEMPLATE_STARTER,
  GLOBAL_CUSTOM_TEMPLATE_VARIABLES,
  GlobalSettingsFormShape,
} from './global-settings.model';

interface EventCallbackRow {
  key: EventCallbackKey;
  label: string;
  enabled: FormControl<boolean>;
  code: FormControl<string>;
  starterTemplate: string;
  variables: CustomCodeVariableDoc[];
}

interface CodeSlotRow {
  key: CodeSlotKey;
  label: string;
  enabled: FormControl<boolean>;
  code: FormControl<string>;
}

@Component({
  selector: 'smart-cart-form-global-settings',
  templateUrl: './smart-cart-form-global-settings.component.html',
  styleUrls: ['./smart-cart-form-global-settings.component.scss'],
  standalone: false,
})
export class SmartCartFormGlobalSettingsComponent implements OnInit {
  @Input({ required: true }) formGroup!: FormGroup<GlobalSettingsFormShape>;

  eventCallbacks: EventCallbackRow[] = [];
  codeSlots: CodeSlotRow[] = [];
  cartPageExpanded = true;
  buttonDesignExpanded = true;
  eventCallbacksExpanded = true;
  customCssExpanded = true;
  codeSlotsExpanded = true;
  overrideExpanded = true;
  customCssError: string | null = null;

  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  // one in-memory previous version per field, lost on reload
  private readonly previousSavedCodeByControl = new WeakMap<FormControl<string>, string>();

  ngOnInit(): void {
    const callbacks = this.formGroup.controls.eventCallbacks.controls;
    this.eventCallbacks = [
      this.buildRow('init', 'Init Callback', callbacks.init),
      this.buildRow('ready', 'Ready Callback', callbacks.ready),
      this.buildRow('show', 'Show Callback', callbacks.show),
      this.buildRow('hide', 'Hide Callback', callbacks.hide),
      this.buildRow('lineItemIncrease', 'LineItemIncrease Callback', callbacks.lineItemIncrease),
      this.buildRow('lineItemDecrease', 'LineItemDecrease Callback', callbacks.lineItemDecrease),
      this.buildRow('lineItemRemove', 'LineItemRemoved Callback', callbacks.lineItemRemove),
    ];

    for (const row of this.eventCallbacks) {
      if (!row.code.value) {
        row.code.setValue(row.starterTemplate);
      }
    }

    const slots = this.formGroup.controls.codeSlots.controls;
    this.codeSlots = CODE_SLOT_KEYS_IN_DISPLAY_ORDER.map((key) => ({
      key,
      label: CODE_SLOT_LABELS[key],
      enabled: slots[key].controls.enabled,
      code: slots[key].controls.code,
    }));

    const cssControl = this.formGroup.controls.customCss.controls.css;
    this.customCssError = this.readCustomCssError();
    cssControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.customCssError = this.readCustomCssError();
    });
  }

  toggleCartPage(): void {
    this.cartPageExpanded = !this.cartPageExpanded;
  }

  toggleButtonDesign(): void {
    this.buttonDesignExpanded = !this.buttonDesignExpanded;
  }

  toggleEventCallbacks(): void {
    this.eventCallbacksExpanded = !this.eventCallbacksExpanded;
  }

  toggleCustomCss(): void {
    this.customCssExpanded = !this.customCssExpanded;
  }

  toggleCodeSlots(): void {
    this.codeSlotsExpanded = !this.codeSlotsExpanded;
  }

  toggleOverride(): void {
    this.overrideExpanded = !this.overrideExpanded;
  }

  onEditCustomCss(): void {
    this.openCodeDialog(this.formGroup.controls.customCss.controls.css, 'css', CUSTOM_CSS_STARTER);
  }

  // slots are raw markup, so a pasted snippet containing a literal `{{` must still save
  onEditCodeSlot(slot: CodeSlotRow): void {
    this.openCodeDialog(slot.code, 'html', '', [], false);
  }

  onEditGlobalCustomTemplate(): void {
    this.openCodeDialog(
      this.formGroup.controls.override.controls.customTemplate.controls.html,
      'html',
      GLOBAL_CUSTOM_TEMPLATE_STARTER,
      GLOBAL_CUSTOM_TEMPLATE_VARIABLES,
    );
  }

  onCustomTemplateToggle(): void {
    const enabledControl = this.formGroup.controls.override.controls.customTemplate.controls.enabled;

    if (enabledControl.value) {
      enabledControl.setValue(false);
      return;
    }

    this.dialog
      .open<SmartCartFormCustomTemplateConfirmDialogComponent, undefined, boolean>(
        SmartCartFormCustomTemplateConfirmDialogComponent,
        { width: '744px', maxWidth: '95vw' },
      )
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (!confirmed) return;
        // seed only on opt-in: a blank html reads as "not active", which stops a live cart blanking
        const htmlControl = this.formGroup.controls.override.controls.customTemplate.controls.html;
        if (!htmlControl.value) {
          htmlControl.setValue(GLOBAL_CUSTOM_TEMPLATE_STARTER);
        }
        enabledControl.setValue(true);
      });
  }

  onEditCallbackCode(callback: EventCallbackRow): void {
    this.openCodeDialog(callback.code, 'javascript', callback.starterTemplate, callback.variables);
  }

  private openCodeDialog(
    codeControl: FormControl<string>,
    language: MonacoEditorLanguage,
    defaultCode: string,
    variables: CustomCodeVariableDoc[] = [],
    validateHandlebarsOnSave?: boolean,
  ): void {
    this.dialog
      .open<SmartCartFormCustomCodeDialogComponent, CustomCodeDialogData, CustomCodeDialogResult | null>(
        SmartCartFormCustomCodeDialogComponent,
        {
          ...CUSTOM_CODE_DIALOG_CONFIG,
          data: {
            code: codeControl.value,
            defaultCode,
            previousCode: this.previousSavedCodeByControl.get(codeControl) ?? '',
            language,
            variables,
            validateHandlebarsOnSave,
          },
        },
      )
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result) return;
        if (result.code === codeControl.value) return;
        this.previousSavedCodeByControl.set(codeControl, codeControl.value);
        codeControl.setValue(result.code);
      });
  }

  private readCustomCssError(): string | null {
    const errors = this.formGroup.controls.customCss.controls.css.errors;
    return (errors?.['disallowedCssDirective'] as string | undefined) ?? null;
  }

  private buildRow(
    key: EventCallbackKey,
    label: string,
    group: FormGroup<{ enabled: FormControl<boolean>; code: FormControl<string> }>,
  ): EventCallbackRow {
    return {
      key,
      label,
      enabled: group.controls.enabled,
      code: group.controls.code,
      starterTemplate: CALLBACK_STARTERS[key],
      variables: CALLBACK_VARIABLES[key],
    };
  }
}
